import { useUserContext } from 'providers/managers/user-context-manager/user-context-manager';
import userInfoStyles from './user-info.module.scss';
import { getLocalJwtFromSessionStorage, useAuth } from 'auth';
import { Button, BUTTON_SIZE, Icon, ICONS_ENUM } from '@sanlam-glacier/app-common/components';
import { useConfig } from 'providers/config/ConfigProvider';
import { displayToast } from '@sanlam-glacier/app-common/modules/toasts';

export const DevToolsUserInfo = () => {
  const { getActingAsIntermediaryUser, getCurrentAuthenticatedUserWithFeatures, getUserFeatures } =
    useUserContext();
  const { getSignedInUser, identificationState } = useAuth();
  const { apiUrl: bffUrl } = useConfig();

  const actingAsUser = getActingAsIntermediaryUser();
  const currentUser = getCurrentAuthenticatedUserWithFeatures();
  const features = getUserFeatures();
  const signedInUser = getSignedInUser();
  const localJwtToken = getLocalJwtFromSessionStorage();

  const renderInfoRow = (name: string, value: string | undefined) => {
    return (
      <div className={userInfoStyles.userInfoGridRow}>
        <b className={userInfoStyles.userInfoGridTitle}>{`${name}:`}</b>
        <p>{value ?? 'Not defined'}</p>
      </div>
    );
  };

  const copyToken = async () => {
    if (!localJwtToken) {
      displayToast({
        toastId: 'user-info-jwt-token-copy',
        title: 'No JWT Token',
        message: 'The JWT token cannot be copied to the clipboard.',
        status: 'info'
      });
      return;
    }
    await navigator.clipboard.writeText(localJwtToken);
    displayToast({
      toastId: 'user-info-jwt-token-copy',
      title: 'JWT Token Copied',
      message: 'The JWT token was copied to the clipboard',
      status: 'info'
    });
  };

  const clearLocalStorage = () => {
    sessionStorage.clear();
    location.reload();
  };

  const openBFFSwagger = () => {
    window.open(`${bffUrl}/swagger`, '_blank');
  };

  return (
    <>
      <div className={userInfoStyles.userInfoButtons}>
        <Button
          color={Button.COLOR.ERROR}
          size={BUTTON_SIZE.SMALL}
          title={localJwtToken ?? 'No token found'}
          disabled={!localJwtToken}
          onClick={copyToken}
        >
          Copy JWT
        </Button>
        <Button
          endIcon={<Icon icon={ICONS_ENUM.ACTION_OPEN_IN_NEW} />}
          color={Button.COLOR.ERROR}
          size={BUTTON_SIZE.SMALL}
          onClick={openBFFSwagger}
        >
          Open BFF Swagger
        </Button>
        <Button color={Button.COLOR.ERROR} size={BUTTON_SIZE.SMALL} onClick={clearLocalStorage}>
          Clear Cache & Reload
        </Button>
      </div>
      <div className={userInfoStyles.userInfoGrid}>
        {renderInfoRow('Identification State', identificationState)}
        {renderInfoRow(
          'User',
          currentUser?.firstName && currentUser.lastName
            ? `${currentUser?.firstName} ${currentUser?.lastName}`
            : undefined
        )}
        {renderInfoRow('User email', signedInUser?.email)}
        {renderInfoRow('User Code', currentUser?.intermediaryCode)}
        {renderInfoRow(
          'Acting as User',
          actingAsUser?.firstName && actingAsUser.lastName
            ? `${actingAsUser?.firstName} ${actingAsUser?.lastName}`
            : undefined
        )}
        {renderInfoRow('Acting as User Code', actingAsUser?.intermediaryCode)}
        {renderInfoRow('User Features', currentUser?.features.join('\n'))}
        {renderInfoRow('Acting as User Features', actingAsUser?.features.join('\n'))}
        {renderInfoRow('Active Feature Set', features.join('\n'))}
        {renderInfoRow('Gateway Token', signedInUser?.accessToken)}
      </div>
    </>
  );
};
